// src/components/Navbar.jsx
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { lang, toggleLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  const closeMobileMenu = () => {
    setIsMenuOpen(false);
  };

  const isHomePage = location.pathname === "/";

  // Helper for cross-page hash navigation
  const getNavHref = (hash) => {
    return isHomePage ? hash : `/${hash}`;
  };

  const linkClass = (path) =>
    `transition duration-300 hover:text-cyan-600 dark:hover:text-cyan-400 ${
      location.pathname === path
        ? "text-cyan-600 dark:text-cyan-400 font-bold"
        : "text-slate-800 dark:text-white/90 font-semibold"
    }`;

  const anchorClass =
    "text-slate-800 dark:text-white/90 font-semibold transition duration-300 hover:text-cyan-600 dark:hover:text-cyan-400";

  return (
    <nav className="relative z-30 border-b border-slate-200 dark:border-white/10 bg-white/90 dark:bg-[#08131F]/80 backdrop-blur-md transition-colors duration-300">
      {/* TOP BAR */}
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* LOGO */}
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src="/assets/images/henki_logo.svg"
            alt="Henki Logo"
            width={36}
            height={36}
            fetchpriority="high"
            className="w-9 h-9 transition duration-300 group-hover:scale-105"
          />
          <span className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            Henki
          </span>
        </Link>

        {/* HAMBURGER & THEME TOGGLE (MOBILE) */}
        <div className="flex items-center gap-2 md:hidden">
          {/* Theme Toggle Mobile */}
          <button
            onClick={toggleTheme}
            className="px-3 py-1.5 rounded-xl border border-slate-300 dark:border-white/15 bg-slate-100 dark:bg-white/10 text-xs font-bold text-slate-900 dark:text-white flex items-center gap-1 shadow-sm"
            title="Alternar Tema"
          >
            <span>{theme === "dark" ? "☀️" : "🌙"}</span>
          </button>

          <button
            type="button"
            onClick={() => setIsMenuOpen((value) => !value)}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-300 dark:border-white/15 bg-slate-100 dark:bg-white/10 text-xl text-slate-900 dark:text-white shadow-sm"
            aria-label="Abrir menú"
          >
            ☰
          </button>
        </div>

        {/* DESKTOP NAV LINKS */}
        <div className="hidden md:flex items-center gap-7 text-sm">
          <Link to="/" className={linkClass("/")}>
            {t("nav.home")}
          </Link>

          <a href={getNavHref("#servicios")} className={anchorClass}>
            {t("nav.services")}
          </a>

          <Link to="/planes" className={linkClass("/planes")}>
            {t("nav.plans")}
          </Link>

          <Link to="/nosotros" className={linkClass("/nosotros")}>
            {t("nav.about")}
          </Link>

          <a href={getNavHref("#comunidad")} className={anchorClass}>
            {t("nav.community")}
          </a>

          {/* CONTROLES: CAMBIO DE IDIOMA & TEMA (DESKTOP) */}
          <div className="flex items-center gap-3 pl-4 border-l border-slate-300 dark:border-white/15">
            {/* Language Switch Button */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-xs font-black text-cyan-700 dark:text-cyan-300 transition shadow-sm"
              title="Cambiar Idioma / Change Language"
            >
              <span>🌐</span>
              <span>{lang === "es" ? "EN" : "ES"}</span>
            </button>

            {/* Theme Toggle Desktop */}
            <button
              onClick={toggleTheme}
              className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-slate-300 dark:border-white/15 bg-slate-100 hover:bg-slate-200 dark:bg-white/10 dark:hover:bg-white/20 text-xs font-bold text-slate-900 dark:text-white transition shadow-sm"
            >
              <span>{theme === "dark" ? `☀️ ${t("nav.themeLight")}` : `🌙 ${t("nav.themeDark")}`}</span>
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE MENU DROPDOWN */}
      {isMenuOpen && (
        <div className="md:hidden mx-6 mb-4 rounded-2xl border border-slate-200 dark:border-white/15 bg-white dark:bg-[#132A41]/95 px-5 py-5 shadow-2xl backdrop-blur-md">
          <div className="flex flex-col gap-4 text-base font-semibold">
            <Link to="/" onClick={closeMobileMenu} className="text-slate-900 dark:text-white">
              {t("nav.home")}
            </Link>

            <a href={getNavHref("#servicios")} onClick={closeMobileMenu} className="text-slate-800 dark:text-white/85">
              {t("nav.services")}
            </a>

            <Link to="/planes" onClick={closeMobileMenu} className={linkClass("/planes")}>
              {t("nav.plans")}
            </Link>

            <Link to="/nosotros" onClick={closeMobileMenu} className={linkClass("/nosotros")}>
              {t("nav.about")}
            </Link>

            <a href={getNavHref("#comunidad")} onClick={closeMobileMenu} className="text-slate-800 dark:text-white/85">
              {t("nav.community")}
            </a>

            <div className="pt-3 border-t border-slate-200 dark:border-white/15 grid grid-cols-2 gap-2">
              <button
                onClick={toggleLanguage}
                className="py-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-xs font-black text-cyan-700 dark:text-cyan-300 flex items-center justify-center gap-1.5"
              >
                <span>🌐</span>
                <span>{lang === "es" ? "English (EN)" : "Español (ES)"}</span>
              </button>

              <button
                onClick={toggleTheme}
                className="py-2.5 rounded-xl bg-slate-100 dark:bg-white/10 border border-slate-300 dark:border-white/15 text-xs font-bold text-slate-900 dark:text-white flex items-center justify-center gap-1.5"
              >
                <span>{theme === "dark" ? "☀️ Claro" : "🌙 Oscuro"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;